// Console interface + hybrid responder wiring.
//
// On boot: register the hybrid responder. It ALWAYS tries the AI
// (the Pages Function proxy attaches HF_TOKEN server-side), and
// falls back to the stub on any failure — transport error, parse
// failure, schema validation, anything. The user is never asked for
// a token; the proxy handles auth. Local dev (vite) has no proxy,
// so the AI call 404s and the loop runs in stub mode automatically.

import {
  cheat,
  getState,
  pullAndon,
  reset,
  setResponder,
  submitGuess,
} from "./andon-loop";
import { stubResponder } from "./stub-responder";
import { aiResponder, getLastAiCall } from "./ai-responder";
import { setResponderMode } from "../components/AiThinkingPanel";
import { formatRuleset, type Rule } from "./dsl";
import type { State } from "./andon-loop";

async function hybridResponder(state: Readonly<State>): Promise<Rule> {
  setResponderMode("ai-busy");
  try {
    const rule = await aiResponder(state);
    setResponderMode("ai-ok");
    return rule;
  } catch (e) {
    console.warn("[hybrid] AI responder failed, falling back to stub:", e);
    setResponderMode("ai-error");
    return stubResponder(state);
  }
}

setResponder(hybridResponder);

declare global {
  interface Window {
    demo?: {
      guess:      (pw: string) => Promise<boolean>;
      cheat:      () => Promise<void>;
      inspect:    () => string;
      rules:      () => string;
      ledger:     () => string;
      pull:       (note?: string) => Promise<void>;
      reset:      () => void;
      state:      () => ReturnType<typeof getState>;
      lastAiCall: () => ReturnType<typeof getLastAiCall>;
    };
  }
}

function fmtLedger(): string {
  const s = getState();
  if (s.ledger.length === 0) return "(ledger empty — nothing has happened yet)";
  return s.ledger
    .map((e) => {
      const time = new Date(e.ts).toISOString().slice(11, 19);
      const hashShort = e.hash.slice(0, 7);
      const payloadStr = JSON.stringify(e.payload);
      return `${String(e.seq).padStart(3, " ")}  ${time}  ${hashShort}  ${e.kind.padEnd(20, " ")}  ${payloadStr}`;
    })
    .join("\n");
}

if (typeof window !== "undefined") {
  window.demo = {
    guess:      (pw) => submitGuess(pw),
    cheat:      () => cheat(),
    inspect:    () => getState().currentPassword,
    rules:      () => formatRuleset(getState().rules),
    ledger:     () => fmtLedger(),
    pull:       (note) => pullAndon("manual_pull", note ? { note } : {}),
    reset:      () => reset(),
    state:      () => getState(),
    lastAiCall: () => getLastAiCall(),
  };
  console.info("[andon-loop demo] window.demo ready — try window.demo.rules()");
}
